// useNotifications Hook
// ─────────────────────────────────────────────────────────────
// Manages push notification opt-in, FCM token lifecycle,
// notification preferences, in-app notification feed,
// and foreground message handling.

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, requestPushPermission, onForegroundMessage, getPushPermissionStatus, isPushMessagingSupported, resolveVapidKey, getExistingPushToken } from '../config/firebase';
import { useAuth } from './useAuth';
import type {
  NotificationDocument,
  NotificationPreferences,
  DEFAULT_NOTIFICATION_PREFERENCES,
} from '../types/notifications';

// Re-export default prefs for convenience
export { DEFAULT_NOTIFICATION_PREFERENCES } from '../types/notifications';

export type PushBlockReason = 'denied' | 'unsupported' | 'vapid_missing' | null;

interface UseNotificationsReturn {
  // Notification feed
  notifications: NotificationDocument[];
  unreadCount: number;
  loading: boolean;

  // Push notification state
  pushPermission: 'granted' | 'denied' | 'default' | 'unsupported';
  pushSupported: boolean;
  vapidConfigured: boolean;
  pushBlockReason: PushBlockReason;
  pushEnabled: boolean;
  requestingPush: boolean;

  // Preferences
  preferences: NotificationPreferences | null;
  preferencesLoading: boolean;

  // Actions
  enablePush: () => Promise<boolean>;
  disablePush: () => Promise<void>;
  updatePreferences: (prefs: NotificationPreferences) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;

  // Foreground message
  latestForegroundMessage: any | null;
}

export function useNotifications(notificationLimit = 50): UseNotificationsReturn {
  const { user } = useAuth();

  // Notification feed
  const [notifications, setNotifications] = useState<NotificationDocument[]>([]);
  const [loading, setLoading] = useState(true);

  // Push state
  const [pushPermission, setPushPermission] = useState<'granted' | 'denied' | 'default' | 'unsupported'>(
    getPushPermissionStatus()
  );
  const [pushSupported, setPushSupported] = useState(true);
  const [vapidConfigured, setVapidConfigured] = useState(!!import.meta.env.VITE_FIREBASE_VAPID_KEY);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [requestingPush, setRequestingPush] = useState(false);
  const fcmTokenRef = useRef<string | null>(null);

  const pushBlockReason: PushBlockReason =
    pushPermission === 'unsupported'
      ? 'unsupported'
      : pushPermission === 'denied'
        ? 'denied'
        : !vapidConfigured
          ? 'vapid_missing'
          : null;

  // ── Detect browser push support + VAPID availability ───────
  // Re-runs when auth changes: the VAPID lookup callable requires a signed-in
  // caller, so an anonymous first pass would otherwise leave push looking broken.
  useEffect(() => {
    let cancelled = false;

    async function detectPushEnvironment() {
      const supported = await isPushMessagingSupported();
      if (cancelled) return;

      setPushSupported(supported);
      setPushPermission(supported ? getPushPermissionStatus() : 'unsupported');

      if (!supported || !user) return;

      const vapidKey = await resolveVapidKey();
      if (cancelled) return;

      setVapidConfigured(!!vapidKey);
    }

    detectPushEnvironment();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Preferences
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [preferencesLoading, setPreferencesLoading] = useState(true);

  // Foreground messages
  const [latestForegroundMessage, setLatestForegroundMessage] = useState<any | null>(null);

  // ── Load notification preferences ─────────────────────────
  useEffect(() => {
    if (!user) {
      setPreferences(null);
      setPreferencesLoading(false);
      return;
    }

    const loadPreferences = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();

        if (data?.notificationPreferences) {
          setPreferences(data.notificationPreferences as NotificationPreferences);
          setPushEnabled(data.notificationPreferences.channels?.push === true);
        } else {
          // Use defaults
          const { DEFAULT_NOTIFICATION_PREFERENCES: defaults } = await import('../types/notifications');
          setPreferences(defaults);
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setPreferencesLoading(false);
      }
    };

    loadPreferences();
  }, [user]);

  // ── Subscribe to in-app notifications ─────────────────────
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(notificationLimit)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as NotificationDocument[];

        setNotifications(notifs);
        setLoading(false);
      },
      (error) => {
        console.error('Failed to subscribe to notifications:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, notificationLimit]);

  // ── Listen for foreground messages ────────────────────────
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      setLatestForegroundMessage(payload);

      // Auto-clear after 10 seconds
      setTimeout(() => {
        setLatestForegroundMessage((current: any) =>
          current === payload ? null : current
        );
      }, 10000);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // ── Computed: unread count ────────────────────────────────
  const unreadCount = notifications.filter((n) => !n.read).length;

  // ── Enable push notifications ─────────────────────────────
  const enablePush = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    setRequestingPush(true);

    try {
      // Request browser permission + get FCM token
      const token = await requestPushPermission();

      if (!token) {
        setPushPermission(getPushPermissionStatus());
        setRequestingPush(false);
        return false;
      }

      fcmTokenRef.current = token;

      // Save token to backend
      const saveFcmTokenFn = httpsCallable(functions, 'saveFcmToken');
      await saveFcmTokenFn({
        token,
        deviceInfo: {
          platform: 'web',
          browser: navigator.userAgent,
          os: navigator.platform,
        },
      });

      // Update preferences to enable push channel
      const updatedPrefs: NotificationPreferences = {
        ...(preferences || ({} as NotificationPreferences)),
        enabled: true,
        channels: {
          ...(preferences?.channels || { push: false, email: true, sms: false }),
          push: true,
        },
      } as NotificationPreferences;

      const updatePrefsFn = httpsCallable(functions, 'updateNotificationPreferences');
      await updatePrefsFn({ preferences: updatedPrefs });

      setPreferences(updatedPrefs);
      setPushEnabled(true);
      setPushPermission('granted');
      setRequestingPush(false);

      return true;
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      setRequestingPush(false);
      return false;
    }
  }, [user, preferences]);

  // ── Disable push notifications ────────────────────────────
  const disablePush = useCallback(async (): Promise<void> => {
    if (!user) return;

    try {
      // Remove FCM token from backend. After a reload the ref is empty, so fall
      // back to reading the token already issued to this device.
      const token = fcmTokenRef.current || (await getExistingPushToken());
      if (token) {
        const removeFcmTokenFn = httpsCallable(functions, 'removeFcmToken');
        await removeFcmTokenFn({ token });
        fcmTokenRef.current = null;
      }

      // Update preferences to disable push
      const updatedPrefs: NotificationPreferences = {
        ...(preferences || ({} as NotificationPreferences)),
        channels: {
          ...(preferences?.channels || { push: false, email: true, sms: false }),
          push: false,
        },
      } as NotificationPreferences;

      const updatePrefsFn = httpsCallable(functions, 'updateNotificationPreferences');
      await updatePrefsFn({ preferences: updatedPrefs });

      setPreferences(updatedPrefs);
      setPushEnabled(false);
    } catch (error) {
      console.error('Failed to disable push notifications:', error);
    }
  }, [user, preferences]);

  // ── Update full preferences ───────────────────────────────
  const updatePreferences = useCallback(
    async (newPrefs: NotificationPreferences): Promise<void> => {
      if (!user) return;

      try {
        const updatePrefsFn = httpsCallable(functions, 'updateNotificationPreferences');
        await updatePrefsFn({ preferences: newPrefs });

        setPreferences(newPrefs);
        setPushEnabled(newPrefs.channels.push);
      } catch (error) {
        console.error('Failed to update preferences:', error);
        throw error;
      }
    },
    [user]
  );

  // ── Mark notification as read ─────────────────────────────
  const markAsRead = useCallback(
    async (notificationId: string): Promise<void> => {
      if (!user) return;

      try {
        const markReadFn = httpsCallable(functions, 'markNotificationRead');
        await markReadFn({ notificationId });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    },
    [user]
  );

  // ── Mark all notifications as read ────────────────────────
  const markAllAsRead = useCallback(async (): Promise<void> => {
    if (!user) return;

    try {
      const markAllReadFn = httpsCallable(functions, 'markAllNotificationsRead');
      await markAllReadFn({});
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    pushPermission,
    pushSupported,
    vapidConfigured,
    pushBlockReason,
    pushEnabled,
    requestingPush,
    preferences,
    preferencesLoading,
    enablePush,
    disablePush,
    updatePreferences,
    markAsRead,
    markAllAsRead,
    latestForegroundMessage,
  };
}
